// Use same-origin by default (recommended behind Nginx `/api` proxy).
// Optional override: REACT_APP_API_BASE_URL (example: /api or https://api.example.com)
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").trim();

function normalizePath(path) {
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
}

function buildApiPath(path) {
  const normalizedPath = normalizePath(path);
  if (!API_BASE_URL) return normalizedPath;

  const normalizedBase = API_BASE_URL.startsWith("/")
    ? API_BASE_URL.replace(/\/+$/, "")
    : API_BASE_URL;

  if (
    normalizedPath === normalizedBase ||
    normalizedPath.startsWith(`${normalizedBase}/`)
  ) {
    return normalizedPath;
  }

  return `${normalizedBase}${normalizedPath}`;
}

/**
 * Build full URL with query params safely.
 * @param {string} path
 * @param {Record<string, any>} [query]
 */
function buildUrl(path, query) {
  const apiPath = buildApiPath(path);
  const url = new URL(apiPath, window.location.origin);
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") return;
      url.searchParams.set(key, String(value));
    });
  }
  return url.toString();
}

/**
 * Basic API error that preserves status & body.
 */
export class ApiError extends Error {
  constructor(message, { status, body } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

/**
 * GET JSON helper
 * @template T
 * @param {string} path
 * @param {Record<string, any>} [query]
 * @returns {Promise<T>}
 */
export async function getJson(path, query) {
  const url = buildUrl(path, query);

  const res = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
  });

  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (!res.ok) {
    const msg =
      (body && (body.message || body.error)) ||
      `Request failed: ${res.status} ${res.statusText}`;
    throw new ApiError(msg, { status: res.status, body });
  }

  return body;
}
